import { z } from 'zod';
import { formVisibility } from './schema.js';
/** Admin UI language. Separate concern from the form's own (config-driven) language. */
export const adminLanguage = z.enum(['es', 'pt', 'en']);
/**
 * App-level settings, stored on the store row (not inside the form config JSON).
 * `visibility` is mirrored into the form config so the storefront knows where it may
 * render, but the store row is the source of truth.
 */
export const storeSettings = z.object({
    app_enabled: z.boolean(),
    visibility: formVisibility,
    admin_language: adminLanguage,
    /**
     * READ-ONLY. The store's own country (ISO 3166-1 alpha-2, e.g. "CO"), sourced from
     * Nuvemshop `GET /store` at install — not a merchant setting, which is why it is excluded
     * from the patch shape below. Null when the install-time lookup failed or predates this
     * column; consumers must degrade gracefully rather than assume it is present.
     * Used by the Coverage tab to only offer datasets for the country the merchant sells in.
     */
    country: z.string().length(2).nullable(),
    /**
     * READ-ONLY. The store's BUYER-facing language (Nuvemshop `main_language`, e.g. "es"), also
     * from the install-time `GET /store`. Distinct from `admin_language`, which is the language
     * the merchant browses this dashboard in — a Brazilian merchant may well read the admin in
     * English. Null when the install-time lookup failed or predates this column.
     * Used to pick the locale of the order-confirmation templates.
     */
    store_language: z.string().nullable().default(null),
    /**
     * READ-ONLY. The store's main currency (ISO 4217, e.g. "COP"), from the same install-time
     * `GET /store`. Null when that lookup failed or predates this column.
     *
     * 🚨 The admin needs this to FORMAT money the way the buyer will see it, and the currency —
     * not the locale — is what decides the decimal places: `Intl` renders COP, CLP and PYG with
     * no cents at all. The Form Designer preview hardcoded ARS before this, so a COP merchant
     * previewing a 10.45 product saw "$ 10,45" while their storefront rendered "COP 10".
     */
    store_currency: z.string().nullable().default(null),
    /**
     * Has the merchant closed the dashboard welcome banner? Stored on the store row rather than
     * in the browser so the dismissal follows the store, not the device — a merchant who opens
     * the app from a second browser should not be welcomed again.
     */
    welcome_dismissed: z.boolean().default(false),
});
/** Patch shape accepted by PUT /api/settings. Every field optional; `country`,
 * `store_language` and `store_currency` are not merchant-editable, so they are omitted rather
 * than made optional. */
export const storeSettingsPatch = storeSettings
    .omit({ country: true, store_language: true, store_currency: true })
    .partial();
export const defaultStoreSettings = {
    app_enabled: true,
    visibility: 'product',
    admin_language: 'es',
    country: null,
    store_language: null,
    store_currency: null,
    welcome_dismissed: false,
};
//# sourceMappingURL=settings.js.map